'use client';

import React, { useLayoutEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useInView } from '@/lib/useInView';
import { trackEvent } from '@/lib/analytics';

gsap.registerPlugin(ScrollTrigger);

type BannerContactProps = {
  heading: string;
  underlinedWord: string;
  underlinePath: string;
  eyebrow?: string;
  email: string;
  emailHref: string;
  pathname?: string;
  className?: string;
  backgroundColor?: string;
  onEmailClick?: () => void;
};

export default function BannerContact({
  heading,
  underlinedWord,
  underlinePath,
  eyebrow = 'Get in touch',
  email,
  emailHref,
  pathname,
  className = '',
  backgroundColor = '#FFE567',
  onEmailClick,
}: BannerContactProps) {
  const underlinedWordRef = useRef<HTMLSpanElement>(null);
  const underlinePathRef = useRef<SVGPathElement>(null);
  const { ref: bannerContainerRef } = useInView<HTMLElement>();

  // Animate underline - hand-drawn stroke effect
  useLayoutEffect(() => {
    if (!underlinePathRef.current) return;

    const path = underlinePathRef.current;
    const pathLength = path.getTotalLength();

    // Set up stroke for drawing animation - stroke only
    gsap.set(path, {
      strokeDasharray: pathLength,
      strokeDashoffset: pathLength,
    });

    const context = gsap.context(() => {
      gsap.to(path, {
        strokeDashoffset: 0,
        duration: 1.2,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: underlinedWordRef.current,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });
    });

    return () => context.revert();
  }, []);

  const handleEmailClick = () => {
    if (onEmailClick) {
      onEmailClick();
    } else if (pathname) {
      trackEvent('what_we_do_click', {
        click_text: email,
        click_url: emailHref,
        page_path: pathname,
      });
    }
  };

  // Split heading to insert underlined word
  const headingParts = heading.split(underlinedWord);
  let beforeWord = headingParts[0];
  const afterWord = headingParts[1] || '';

  // Extract the last word from beforeWord to keep it with underlinedWord
  // This prevents line breaks between the last word and the underlined word
  const beforeWordTrimmed = beforeWord.trimEnd();
  const lastSpaceIndex = beforeWordTrimmed.lastIndexOf(' ');
  const lastWord = lastSpaceIndex >= 0 && lastSpaceIndex < beforeWordTrimmed.length - 1
    ? beforeWordTrimmed.substring(lastSpaceIndex + 1)
    : null;
  const beforeWordRest = lastWord 
    ? beforeWordTrimmed.substring(0, lastSpaceIndex + 1)
    : beforeWord;

  return (
    <div className={`global-padding ${className}`}>
      <section
        ref={bannerContainerRef}
        className="banner-link-container animate-in-view md:mx-auto px-[1.875rem] py-[2.5rem] md:px-[3.125rem] md:py-[4.375rem] max-[399px]:px-[1rem] rounded-3xl flex flex-col md:flex-row items-start md:items-center"
        style={{ backgroundColor }}
        aria-labelledby="contact-partner-heading"
        tabIndex={-1}
      >
        <div className="md:w-1/2 w-full mb-8 md:mb-0">
          <h2
            id="contact-partner-heading"
            className="body-xl-display mb-4 text-left pr-6"
          >
            {beforeWordRest}
            {lastWord ? (
              <span className="whitespace-nowrap">
                {lastWord}{' '}
                <span ref={underlinedWordRef} className="relative inline-block">
                  {underlinedWord}
                  <svg
                    className="absolute -bottom-1 left-0 w-full h-auto max-w-[13.25rem]"
                    width="212"
                    height="7"
                    viewBox="0 0 212 7"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      ref={underlinePathRef}
                      d={underlinePath}
                      stroke="#E60000"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </span>
            ) : (
              <span ref={underlinedWordRef} className="relative inline-block">
                {underlinedWord}
                <svg
                  className="absolute -bottom-1 left-0 w-full h-auto max-w-[212px]"
                  width="212"
                  height="7"
                  viewBox="0 0 212 7"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    ref={underlinePathRef}
                    d={underlinePath}
                    stroke="#E60000"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            )}
            {afterWord}
          </h2>
        </div>
        <div className="md:w-1/2 w-full flex flex-col">
          <div className="md:ml-auto">
            <p className="eyebrow-lg">
              {eyebrow}
            </p>
            <a
              href={emailHref}
              className="body-lg-md-xl block mb-1 animated-underline-link focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              aria-label={`Send email to ${email}`}
              onClick={handleEmailClick}
            >
              {email}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

